import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import * as userAuthActions from './user-auth.actions';
import { UserAuthState, initialState } from './user-auth.state';

const mergeState = (state: UserAuthState, patch: Partial<UserAuthState>): UserAuthState => ({
  ...state,
  ...patch,
});

const reducer: ActionReducer<UserAuthState, Action> = createReducer<UserAuthState>(
  initialState,
  on(
    userAuthActions.activateAccount,
    (state): UserAuthState =>
      mergeState(state, {
        loading: true,
        errorMessage: null,
      })
  ),
  on(
    userAuthActions.activateAccountSuccess,
    (state): UserAuthState =>
      mergeState(state, {
        loading: false,
        errorMessage: null,
      })
  ),
  on(
    userAuthActions.activateAccountError,
    (state, { errorMessage }): UserAuthState =>
      mergeState(state, {
        errorMessage,
        loading: false,
      })
  ),

  on(
    userAuthActions.login,
    (state): UserAuthState =>
      mergeState(state, {
        loading: true,
        errorMessage: null,
        mfaErrorMessage: null,
      })
  ),
  on(
    userAuthActions.loginSuccess,
    (state): UserAuthState =>
      mergeState(state, {
        loading: false,
        errorMessage: null,
        mfaErrorMessage: null,
        loginStep: 'CREDENTIALS',
        pendingLoginId: null,
        maskedPhoneNumber: null,
      })
  ),
  on(
    userAuthActions.loginMfaRequired,
    (state, { pendingLoginId, maskedPhoneNumber }): UserAuthState =>
      mergeState(state, {
        loading: false,
        errorMessage: null,
        mfaErrorMessage: null,
        loginStep: 'MFA',
        pendingLoginId,
        maskedPhoneNumber,
      })
  ),
  on(
    userAuthActions.loginError,
    (state, { errorMessage }): UserAuthState =>
      mergeState(state, {
        loading: false,
        myself: null,
        errorMessage,
        loginStep: 'CREDENTIALS',
        pendingLoginId: null,
        maskedPhoneNumber: null,
        mfaErrorMessage: null,
      })
  ),
  on(
    userAuthActions.validateLoginMfa,
    (state): UserAuthState =>
      mergeState(state, {
        loading: true,
        mfaErrorMessage: null,
      })
  ),
  on(
    userAuthActions.validateLoginMfaError,
    (state, { errorMessage }): UserAuthState =>
      mergeState(state, {
        loading: false,
        mfaErrorMessage: errorMessage,
      })
  ),
  on(
    userAuthActions.resendLoginMfaCode,
    (state): UserAuthState =>
      mergeState(state, {
        loading: true,
        mfaErrorMessage: null,
      })
  ),
  on(
    userAuthActions.resendLoginMfaCodeSuccess,
    (state, { pendingLoginId, maskedPhoneNumber }): UserAuthState =>
      mergeState(state, {
        loading: false,
        mfaErrorMessage: null,
        loginStep: 'MFA',
        pendingLoginId,
        maskedPhoneNumber,
      })
  ),
  on(
    userAuthActions.resendLoginMfaCodeError,
    (state, { errorMessage }): UserAuthState =>
      mergeState(state, {
        loading: false,
        mfaErrorMessage: errorMessage,
      })
  ),
  on(
    userAuthActions.resetLoginFlow,
    (state): UserAuthState =>
      mergeState(state, {
        loading: false,
        errorMessage: null,
        mfaErrorMessage: null,
        loginStep: 'CREDENTIALS',
        pendingLoginId: null,
        maskedPhoneNumber: null,
      })
  ),

  on(userAuthActions.logout, (state): UserAuthState => mergeState(state, { loading: true })),
  on(
    userAuthActions.logoutSuccess,
    (state): UserAuthState =>
      mergeState(state, {
        loading: false,
        myself: null,
        loginStep: 'CREDENTIALS',
        pendingLoginId: null,
        maskedPhoneNumber: null,
        mfaErrorMessage: null,
      })
  ),
  on(
    userAuthActions.logoutError,
    (state, errorMessage): UserAuthState =>
      mergeState(state, {
        loading: false,
        myself: null,
        loginStep: 'CREDENTIALS',
        pendingLoginId: null,
        maskedPhoneNumber: null,
        mfaErrorMessage: null,
      })
  ),

  on(userAuthActions.getMyself, (state): UserAuthState => mergeState(state, { loading: true })),
  on(
    userAuthActions.getMyselfSuccess,
    (state, { user }): UserAuthState => mergeState(state, { loading: false, myself: user })
  ),
  on(
    userAuthActions.getMyselfError,
    (state, errorMessage): UserAuthState =>
      mergeState(state, {
        myself: null,
        loading: false,
      })
  ),

  on(userAuthActions.updateMyLanguage, (state): UserAuthState => mergeState(state, { loading: true })),
  on(userAuthActions.updateAdminself, (state): UserAuthState => mergeState(state, { loading: true })),
  on(userAuthActions.updateMyself, (state): UserAuthState => mergeState(state, { loading: true })),
  on(
    userAuthActions.updateMyselfSuccess,
    (state, { updatedUser }): UserAuthState =>
      mergeState(state, {
        loading: false,
        myself: state.myself ? { ...updatedUser, company: { ...state.myself.company } } : { ...updatedUser },
      })
  ),
  on(
    userAuthActions.updateMyLanguageSuccess,
    (state, { updatedUser }): UserAuthState =>
      mergeState(state, {
        loading: false,
        myself: state.myself ? ({ ...state.myself, ...updatedUser } as typeof state.myself) : null,
      })
  ),
  on(userAuthActions.updateMyselfError, (state, errorMessage): UserAuthState => mergeState(state, { loading: false })),
  on(userAuthActions.updateMyLanguageError, (state): UserAuthState => mergeState(state, { loading: false })),

  on(
    userAuthActions.getPasswordResetLink,
    (state): UserAuthState => mergeState(state, { loading: true, myself: null, errorMessage: null })
  ),
  on(
    userAuthActions.getPasswordResetLinkSuccess,
    (state): UserAuthState =>
      mergeState(state, {
        loading: false,
        myself: null,
        errorMessage: null,
      })
  ),
  on(
    userAuthActions.getPasswordResetLinkError,
    (state, { errorMessage }): UserAuthState =>
      mergeState(state, {
        loading: false,
        myself: null,
        errorMessage,
      })
  ),

  on(
    userAuthActions.setNewPassword,
    (state): UserAuthState => mergeState(state, { loading: true, myself: null, errorMessage: null })
  ),
  on(
    userAuthActions.setNewPasswordSuccess,
    (state): UserAuthState => mergeState(state, { loading: false, myself: null, errorMessage: null })
  ),
  on(
    userAuthActions.setNewPasswordError,
    (state, { errorMessage }): UserAuthState =>
      mergeState(state, {
        loading: false,
        myself: null,
        errorMessage,
      })
  ),

  on(
    userAuthActions.updateMyInvoiceData,
    (state): UserAuthState => mergeState(state, { loading: true, errorMessage: null })
  ),
  on(
    userAuthActions.updateMyInvoiceDataSuccess,
    (state, { invoiceData }): UserAuthState =>
      mergeState(state, {
        myself: { ...state.myself, invoiceData },
        loading: false,
        errorMessage: null,
      })
  ),
  on(
    userAuthActions.updateMyInvoiceDataError,
    (state): UserAuthState =>
      mergeState(state, {
        loading: false,
        errorMessage: 'ERROR_UPDATING_INVOICE_DATA',
      })
  ),
  on(
    userAuthActions.getBrandingSettings,
    (state): UserAuthState =>
      mergeState(state, {
        loadingBranding: true,
      })
  ),
  on(
    userAuthActions.getBrandingSettingsSuccess,
    (state, { branding }): UserAuthState =>
      mergeState(state, {
        loadingBranding: false,
        branding,
      })
  ),
  on(
    userAuthActions.getBrandingSettingsError,
    (state): UserAuthState =>
      mergeState(state, {
        loadingBranding: false,
        branding: null,
      })
  )
);

export function userAuthReducer(state: UserAuthState | undefined, action: Action): UserAuthState {
  return reducer(state, action);
}
